import { useMemo } from 'react';
import { Product } from '@setvi/shared/interfaces';
import { SInfiniteTable } from '@setvi/shared/components/s-table/infinite';
import { resourceView } from '@setvi/shared/utils/viewer';
import { SText, TableLoader } from '@setvi/shared/components';
import { Box } from '@material-ui/core';
import { useAssociatedResourceColumns } from './useAssociatedResourceColumns';
import { useAssociatedResource } from './useAssociatedResource';
import { ContentTitleContainer } from '../product-info';
import { ProductsEmptyState } from '../../components';

export interface AssociatedResourceProps {
  product: Product;
  closeDialog?: () => void;
  captureResourceAnalytics?: (additionalValues?: {
    [key: string]: string;
  }) => void;
  readOnly?: boolean;
}

export const AssociatedResource = ({
  product,
  readOnly,
  closeDialog,
  captureResourceAnalytics
}: AssociatedResourceProps) => {
  const { resources, isLoading } = useAssociatedResource({ product });
  const { columns } = useAssociatedResourceColumns({
    closeDialog,
    readOnly
  });

  const content = useMemo(() => {
    if (isLoading) return <TableLoader columns={columns} />;

    if (!resources.length && !isLoading)
      return <ProductsEmptyState title="No Resources Available" />;

    return (
      <SInfiniteTable
        newDesign
        stickyHeader={false}
        list={resources}
        columns={columns}
        keyGetter={resource => resource?.ID}
        isLoading={isLoading}
        row={{
          onClick: resource => {
            captureResourceAnalytics?.({ categoryName: resource.CategoryName });
            resourceView({
              name: resource.Name,
              resourceTypeId: resource.ResourceTypeID,
              resourceId: resource.ResourceID
            });
          }
        }}
      />
    );
  }, [isLoading, columns, resources, captureResourceAnalytics]);

  return (
    <Box flex={1} display="flex" flexDirection="column" width="100%">
      <ContentTitleContainer mb={2}>
        <SText title="Resources" weight="semibold" />
      </ContentTitleContainer>
      {content}
    </Box>
  );
};
